import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  getProjectDocument,
  subscribeToProjectStatus,
  subscribeToExportStatus
} from '../project.service';

export const useExport = ({ projectId }) => {
  const [projectData, setProjectData] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exportStatus, setExportStatus] = useState(null);
  const [progress, setProgress] = useState(0);
  const [isUpdating, setIsUpdating] = useState(
    projectData?.exportHistory?.length > 0 && exportStatus !== 'COMPLETED'
  );

  const isCurrentlyUpdating = useRef(isUpdating);
  const totalCuts = useRef(0);
  const actualTotalSteps = useRef(0);
  const finalRenderProgress = useRef(null);
  const isFinalRenderStarted = useRef(false);
  const progressIntervalRef = useRef(null);
  const previousExportStatus = useRef(null);

  const setUpdateState = () => {
    setIsUpdating(true);
    isCurrentlyUpdating.current = true;
    setProgress(0);
    setExportStatus('PENDING');

    // Reset all tracking refs so they start fresh
    totalCuts.current = 0;
    actualTotalSteps.current = 0;
    finalRenderProgress.current = null;
    isFinalRenderStarted.current = false;

    // Clear any running intervals
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  };

  // Reset refs when projectId changes
  useEffect(() => {
    totalCuts.current = 0;
    actualTotalSteps.current = 0;
    finalRenderProgress.current = null;
    isFinalRenderStarted.current = false;
    previousExportStatus.current = null;

    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  }, [projectId]);

  useEffect(() => {
    if (!projectId) {
      setLoading(false);
      return;
    }

    const loadVideoData = async () => {
      try {
        setLoading(true);
        // Get initial project data
        const project = await getProjectDocument(projectId);
        setProjectData(project);

        // Derive updating state from exportHistory and exportStatus
        const updating = project?.exportHistory?.length > 0 && project.exportStatus !== 'COMPLETED';
        setIsUpdating(updating);
        isCurrentlyUpdating.current = updating;

        // Check if video already exists (but not if we're updating)
        if (project.video && project.exportStatus === 'COMPLETED' && !updating) {
          setVideoUrl(project.video);
          setExportStatus('COMPLETED');
          setProgress(100);
          previousExportStatus.current = 'COMPLETED'; // Initialize previous status
          setLoading(false);
          return;
        }

        // Set initial export status
        if (updating) {
          setVideoUrl(project.video); // Keep old video
          setExportStatus('PENDING');
          setProgress(0);
          previousExportStatus.current = project.exportStatus; // Initialize previous status
        } else {
          // Use actual project status, don't default to INITIATED
          setExportStatus(project.exportStatus || null);
          previousExportStatus.current = project.exportStatus; // Initialize previous status
          if (project.video) {
            setVideoUrl(project.video);
          }
        }
        setLoading(false);
      } catch (error) {
        console.error('Load video error:', error);
        toast.error('Failed to load project data');
        setLoading(false);
      }
    };

    loadVideoData();
  }, [projectId]);

  // Listen to real-time project status updates
  useEffect(() => {
    if (!projectId) return;

    const unsubscribe = subscribeToProjectStatus(
      projectId,
      (data) => {
        // Update export status

        if (data.exportStatus) {
          // If we're currently updating, ignore COMPLETED status until we see INITIATED
          if (isCurrentlyUpdating.current && data.exportStatus === 'COMPLETED') {
            return;
          }

          setExportStatus(data.exportStatus);
          if (data.exportStatus === 'INITIATED') {
            toast.loading('Generating video...', {
              id: 'export-status'
            });
            isCurrentlyUpdating.current = false; // Clear guard once INITIATED is received
            previousExportStatus.current = 'INITIATED';
          } else if (data.exportStatus === 'COMPLETED') {
            // Only show notification if transitioning from non-COMPLETED to COMPLETED
            if (previousExportStatus.current !== 'COMPLETED') {
              setProgress(100);
              toast.success('Video ready!', { id: 'export-status' });
            }
            if (data.video) {
              setVideoUrl(data.video);
            }
            // Reset progress tracking refs for next export
            totalCuts.current = 0;
            actualTotalSteps.current = 0;
            finalRenderProgress.current = null;
            isFinalRenderStarted.current = false;
            setIsUpdating(false);
            previousExportStatus.current = 'COMPLETED';
          } else {
            previousExportStatus.current = data.exportStatus;
          }
        }
        // Update project data
        setProjectData(data);
      },
      (error) => {
        console.error('Subscription error:', error);
        toast.error('Failed to get video status updates');
      }
    );

    return () => {
      unsubscribe();
    };
  }, [projectId]);

  // Subscribe to export progress only when export is in progress
  useEffect(() => {
    if (!projectId || exportStatus !== 'INITIATED') return;

    const exportUnSubscribe = subscribeToExportStatus(
      projectId,
      (data) => {
        // Set total steps (cuts + 1 for final render)
        if (data.totalCuts && totalCuts.current === 0) {
          totalCuts.current = data.totalCuts;
          actualTotalSteps.current = data.totalCuts + 1;
        }

        // Calculate progress
        if (data.completedCuts !== undefined && actualTotalSteps.current > 0) {
          let currentProgress = 0;

          if (data.finalRenderLock) {
            // Final render in progress
            if (!isFinalRenderStarted.current) {
              // First time entering final render - initialize from current progress
              const baseProgress = Math.round((totalCuts.current / actualTotalSteps.current) * 100);
              finalRenderProgress.current = baseProgress;
              isFinalRenderStarted.current = true;

              // Start interval to smoothly increment progress from baseProgress to 99%
              progressIntervalRef.current = setInterval(() => {
                setProgress((prevProgress) => {
                  if (prevProgress < 99) {
                    const newProgress = Math.min(99, prevProgress + 1);
                    finalRenderProgress.current = newProgress;
                    return newProgress;
                  } else {
                    clearInterval(progressIntervalRef.current);
                    return prevProgress;
                  }
                });
              }, 500);
            }
            currentProgress = finalRenderProgress.current;
          } else if (data.completedCuts < totalCuts.current) {
            // Processing cuts
            currentProgress = Math.round((data.completedCuts / actualTotalSteps.current) * 100);
          } else {
            // All cuts done, waiting for final render
            currentProgress = Math.round((totalCuts.current / actualTotalSteps.current) * 100);
          }

          setProgress((prevProgress) => Math.max(prevProgress, currentProgress));
        }
      },
      (error) => {
        console.error('Export status subscription error:', error);
      }
    );

    return () => {
      exportUnSubscribe();
      // Clean up progress interval
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, [projectId, exportStatus]);

  return {
    projectData,
    videoUrl,
    loading,
    exportStatus,
    progress,
    isUpdating,
    setIsUpdating,
    setUpdateState
  };
};
